import classnames from "classnames";
import moment from "moment";
import React from "react";
import { TextLink } from "modules/common/components/routing/TextLink";
import { SORTABLE_DATE_FORMAT } from "modules/common/constants";
import { columnLeft, columnsWidth, PlanGrid } from "modules/spending/workload-plan/models/plan-scale";
import { PlanLinks } from "../models/plan-links";
import { PlanOperationInput } from "../models/plan-draft";
import { AbsenceBand, ConflictRange, formatRange } from "../models/plan-conflicts";
import { BusyBand } from "../models/plan-busy";
import { absenceClassName, absenceLabel } from "modules/spending/workload-plan/components/plan-absences";
import { PlanSegment, ProjectPaint, ProjectParticipant } from "../models/project-plan-types";
import { BarMode, DraggableBar } from "./DraggableBar";
import { ConflictZones, PlanTrack, trackStyles } from "./PlanTrack";
import styles from "./ProjectPlanRowView.module.scss";

/**
 * Уже этой ширины подпись в полосу не влезает. Порог держится ниже ширины дня (30px):
 * однодневный отрезок обязан показывать подпись.
 */
const LABEL_MIN_WIDTH = 26;

/** Уже этого номер проекта не влезает и в компактную подпись занятости (неделя: день ~9px) */
const BUSY_LABEL_MIN_WIDTH = 20;

/** Ниже этой ширины подпись рисуется компактно: мельче, без отступов, по центру */
const LABEL_TIGHT_WIDTH = 44;

interface ProjectPlanRowViewProps {
    row: ProjectParticipant;
    paint: ProjectPaint;
    /** Номер проекта — подпись полосы, как в плановом табеле */
    label: string;
    grid: PlanGrid;
    monthBreaks: number[];
    selectedSlot: { start: number; length: number } | null;
    orderId: string;
    links: PlanLinks;
    /** Третий уровень — исполнитель внутри раздела: отступ больше */
    nested?: boolean;
    /** Режим правки и право двигать работы этой строки */
    editable?: boolean;
    /** Операция черновика — сдвиг работы или её края; пока идёт, полоса стоит на новом месте */
    onOperation?: (operation: PlanOperationInput) => Promise<unknown> | void;
    /** Дни конфликтов строки — подсветка поверх полос */
    conflicts?: ConflictRange[];
    /** Раздел строки в режиме «Разделы» — входит в адрес строки для перехода к конфликту */
    sectionId?: string;
    /** К строке перешли из панели конфликтов — подсвечена целиком */
    highlighted?: boolean;
    /** Плановые отсутствия сотрудника — полосами под работами */
    absences?: AbsenceBand[];
    /** Занятость сотрудника на других проектах (и в других разделах) — приглушённым фоном */
    busy?: BusyBand[];
    /** Клик по занятости — перейти к строке сотрудника в том проекте */
    onBusyClick?: (band: BusyBand, employerId: string) => void;
    /** Даты колонок сетки — якорь края отрезка в операции */
    days?: string[];
    /** Правый клик по полосе работы — окно её связей */
    onOpenLinks?: (orderId: string, assignmentId: string, employerId: string) => void;
    /** От полос работ можно протягивать связи (точки у концов рисует LinksLayer) */
    linkConnectable?: boolean;
}

const WORK_KINDS: { [mode in BarMode]: PlanOperationInput["kind"] } = {
    move: "work",
    start: "work-start",
    end: "work-end",
};

/** Строка графика участника проекта. Только показ: ни жестов, ни выделения ячеек */
export const ProjectPlanRowView: React.FC<ProjectPlanRowViewProps> = React.memo((props) => {
    const {
        row,
        paint,
        label,
        grid,
        monthBreaks,
        selectedSlot,
        orderId,
        links,
        nested,
        editable,
        onOperation,
        conflicts,
        sectionId,
        highlighted,
        absences,
        busy,
        onBusyClick,
        days,
        onOpenLinks,
        linkConnectable,
    } = props;

    const openLinks = React.useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            const bar = (event.target as Element).closest("[data-link-end]");
            const key = bar ? bar.getAttribute("data-link-end") || "" : "";

            if (!onOpenLinks || !key.startsWith("work:")) {
                return;
            }

            event.preventDefault();
            onOpenLinks(orderId, key.slice("work:".length), row.id);
        },
        [onOpenLinks, orderId, row.id]
    );

    const outsourced = row.kind === "outsourcer";

    // Занятость — только там, где своей работы нет: совпадение и так видно штриховкой
    // перегрузки, а под своей полосой метка и номер чужого проекта прятались бы
    const freeBusy = React.useMemo(() => {
        if (!busy || !busy.length) {
            return busy;
        }

        const own = new Set<number>();

        row.segments.forEach((segment) => {
            if (!segment.ghost) {
                for (let column = segment.start; column < segment.start + segment.length; column++) {
                    own.add(column);
                }
            }
        });

        const pieces: BusyBand[] = [];

        busy.forEach((band) => {
            let start = -1;

            for (let column = band.start; column <= band.start + band.length; column++) {
                const free = column < band.start + band.length && !own.has(column);

                if (free && start < 0) {
                    start = column;
                } else if (!free && start >= 0) {
                    pieces.push({ ...band, start, length: column - start });
                    start = -1;
                }
            }
        });

        return pieces;
    }, [busy, row.segments]);

    // У подрядчика срок договора — главное, что нужно видеть, поэтому он стоит
    // в липкой колонке рядом с номером договора: у полосы его унесло бы
    // горизонтальной прокруткой. У сотрудника вторая строка — должность.
    const note = outsourced ? withStopDay(row.note, row.segments) : row.note;

    // сотрудник — в свою карточку; подрядчик — сразу в раздел состава проекта,
    // договор и контрагент достижимы оттуда
    const to = outsourced ? links.section(orderId, row.sectionId) : links.employee(row.id);

    return (
        <div
            className={classnames(styles.row, { [styles.highlighted]: highlighted })}
            data-plan-row={planRowKey(orderId, row.id, sectionId)}
        >
            <div
                className={classnames(styles.name, { [styles.nameNested]: nested })}
                data-tip={row.label}
                data-tip-note={note}
            >
                <div className={styles.label}>
                    <TextLink to={to}>{row.label}</TextLink>
                </div>
                <div className={styles.note}>{note}</div>
            </div>

            <PlanTrack
                grid={grid}
                monthBreaks={monthBreaks}
                selectedSlot={selectedSlot}
                className={styles.track}
                conflicts={conflicts}
                onContextMenu={onOpenLinks ? openLinks : undefined}
            >
                {/* Занятость на других проектах — самый нижний слой: не блок проекта, а фон.
                    Где она совпала со своей работой, сверху перегрузка — красная штриховка */}
                {freeBusy &&
                    freeBusy.map((band) => {
                        const width = columnsWidth(grid, band.start, band.length);
                        // свой проект, другой раздел — подпись шифром раздела; чужой — номером проекта
                        const caption = band.orderId === orderId ? band.sectionCode || "Без раздела" : band.inventory;

                        return (
                            <div
                                key={`busy_${band.orderId}_${band.contentGuid}_${band.start}`}
                                className={classnames(trackStyles.busy, {
                                    // в одну-полторы ячейки номер влезает только по центру, без отступа и мельче
                                    [trackStyles.busyTight]: width < LABEL_TIGHT_WIDTH,
                                })}
                                style={{ left: columnLeft(grid, band.start), width }}
                                data-tip={band.orderId === orderId ? `Раздел ${caption}` : band.title}
                                data-tip-note={[
                                    band.orderId !== orderId && band.sectionCode,
                                    formatRange(band.from, band.to),
                                    "клик — к строке сотрудника там",
                                ]
                                    .filter(Boolean)
                                    .join(" · ")}
                                onClick={onBusyClick ? () => onBusyClick(band, row.id) : undefined}
                            >
                                {width >= BUSY_LABEL_MIN_WIDTH && caption}
                            </div>
                        );
                    })}

                {/* отсутствия — под работами: где работа легла на отпуск, её закрывает полоса
                    и штриховка конфликта, а где работы нет — видно, докуда отсутствие тянется */}
                {absences &&
                    absences.map((band) => {
                        const width = columnsWidth(grid, band.start, band.length);

                        return (
                            <div
                                key={`absence_${band.start}`}
                                // вид отсутствия — общий с Табелем План
                                className={classnames(trackStyles.absenceBand, absenceClassName(band.type))}
                                style={{ left: columnLeft(grid, band.start), width }}
                                data-tip={band.type}
                                data-tip-note={formatRange(band.from, band.to)}
                            >
                                {absenceLabel(band.type, "", width)}
                            </div>
                        );
                    })}

                {row.segments.map((segment) => {
                    const left = columnLeft(grid, segment.start);
                    const width = columnsWidth(grid, segment.start, segment.length);

                    const className = classnames(trackStyles.block, {
                        [trackStyles.blockOutsourced]: outsourced,
                        [trackStyles.blockTight]: width < LABEL_TIGHT_WIDTH,
                        [trackStyles.blockDark]: paint.dark,
                        [trackStyles.blockLight]: !paint.dark,
                        [trackStyles.cutLeft]: segment.continuesLeft,
                        [trackStyles.cutRight]: segment.continuesRight,
                        [trackStyles.ghost]: segment.ghost,
                        [trackStyles.proposed]: !!segment.proposed,
                        [trackStyles.blockPast]: segment.isPast && !segment.ghost,
                    });

                    const blockStyle = { backgroundColor: paint.background, borderColor: paint.borderColor };
                    const caption = !segment.ghost && (
                        <>
                            {width >= LABEL_MIN_WIDTH && <span className={trackStyles.blockLabel}>{label}</span>}
                            <ConflictZones
                                grid={grid}
                                start={segment.start}
                                length={segment.length}
                                conflicts={conflicts}
                            />
                        </>
                    );

                    const key = `${segment.proposed ? `proposed_${segment.proposed}_` : segment.ghost ? "ghost_" : ""}${
                        segment.assignmentId || ""
                    }_${segment.start}`;

                    // тянуть можно только работу плана и только её новое положение
                    const assignmentId = segment.assignmentId;

                    // прошлое видно, но не двигается — как в Табеле План
                    if (editable && onOperation && assignmentId && !segment.ghost && !segment.isPast && !outsourced) {
                        // Край — у каждого отрезка: операция несёт якорь (дату края отрезка) и меняет
                        // только его. Поэтому тянется и середина работы с дырой, и левый край текущей
                        // части работы, начавшейся в прошлом (левее — прошлое, туда не пустит правило)
                        const firstDay = days ? days[segment.start] : undefined;
                        const lastDay = days ? days[segment.start + segment.length - 1] : undefined;

                        return (
                            <DraggableBar
                                key={key}
                                grid={grid}
                                start={segment.start}
                                length={segment.length}
                                enabled={true}
                                edges={{
                                    // край за пределами сетки не взять
                                    start: !!firstDay && !segment.continuesLeft,
                                    end: !!lastDay && !segment.continuesRight,
                                }}
                                className={className}
                                style={blockStyle}
                                tip={row.label}
                                tipNote={describe(row.note, segment)}
                                linkEnd={`work:${assignmentId}`}
                                linkConnectable={!!linkConnectable}
                                onCommit={(mode, delta) =>
                                    onOperation({
                                        kind: WORK_KINDS[mode],
                                        assignmentId,
                                        shift: delta,
                                        fromDay: mode === "start" ? firstDay : mode === "end" ? lastDay : null,
                                    })
                                }
                            >
                                {caption}
                            </DraggableBar>
                        );
                    }

                    return (
                        <div
                            key={key}
                            className={className}
                            style={{ ...blockStyle, left, width }}
                            data-link-end={
                                segment.assignmentId && !segment.ghost ? `work:${segment.assignmentId}` : undefined
                            }
                            data-tip={
                                segment.proposed
                                    ? `${row.label} — предложено по связям`
                                    : segment.ghost
                                      ? undefined
                                      : row.label
                            }
                            data-tip-note={
                                segment.proposed
                                    ? "Сдвиг ждёт согласования ГИПа проекта — список в «Согласования» в шапке"
                                    : segment.ghost
                                      ? undefined
                                      : describe(row.note, segment)
                            }
                            data-proposed={
                                segment.proposed && segment.assignmentId ? `work:${segment.assignmentId}` : undefined
                            }
                        >
                            {caption}
                        </div>
                    );
                })}
            </PlanTrack>
        </div>
    );
});

ProjectPlanRowView.displayName = "ProjectPlanRowView";

/** Адрес строки исполнителя на странице: проект / сотрудник / раздел (пусто — режим «Сотрудники») */
export function planRowKey(orderId: string, employerId: string, sectionId?: string | null): string {
    return `${orderId}/${employerId}/${sectionId || ""}`;
}

/** Номер договора со сроком: «Договор 457 · до 30.10.2026» */
function withStopDay(note: string, segments: PlanSegment[]): string {
    const last = segments.reduce((result, segment) => (segment.stopDay > result ? segment.stopDay : result), "");

    if (!last) {
        return note;
    }

    const date = `до ${moment(last, SORTABLE_DATE_FORMAT).format("DD.MM.YYYY")}`;

    return note ? `${note} · ${date}` : date;
}

/**
 * Подсказка отрезка. Срок берётся из настоящей длины, а не из видимой: у договора,
 * который не помещается в окно, иначе показывалось бы «сколько влезло на экран».
 */
function describe(note: string, segment: PlanSegment): string {
    const parts: string[] = [];

    if (note) {
        parts.push(note);
    }

    if (segment.workDays > 0) {
        parts.push(`${segment.workDays} раб. дн.`);
    }

    if (segment.stopDay) {
        parts.push(`до ${moment(segment.stopDay, SORTABLE_DATE_FORMAT).format("DD.MM.YYYY")}`);
    }

    return parts.join(" · ");
}
